export const FOUNDERS_FAQS = [
  {
    question: "How does GAIA help startup founders save time?",
    answer:
      "GAIA acts as a proactive AI chief of staff — it monitors your email, Slack, GitHub, and CRM around the clock, handles repetitive operational tasks, and delivers concise briefings. Founders typically save 8-12 hours per week on investor updates, pipeline reviews, and team communication.",
  },
  {
    question: "Can GAIA write investor updates automatically?",
    answer:
      "Yes. GAIA connects to your Google Sheets, Notion, and email to pull the latest metrics (MRR, churn, pipeline), drafts the investor update in your voice, and sends it on schedule. You spend 5 minutes reviewing instead of 2 hours writing.",
  },
  {
    question: "What tools does GAIA integrate with for founders?",
    answer:
      "GAIA integrates with Gmail, Slack, Google Calendar, GitHub, HubSpot, Notion, Google Sheets, Linear, and 30+ other tools founders use daily. It creates an intelligent automation layer across your entire stack.",
  },
  {
    question: "Is GAIA a replacement for hiring an EA or chief of staff?",
    answer:
      "GAIA handles the operational and information work that an EA would manage — briefings, updates, follow-ups, monitoring — at a fraction of the cost. It's ideal for early-stage founders who need operational leverage without the overhead of a full-time hire.",
  },
  {
    question: "How does GAIA's morning briefing work for founders?",
    answer:
      "Every morning at your chosen time, GAIA scans your inbox, calendar events, Slack activity, and GitHub notifications from the past 24 hours and delivers one crisp summary. No tabs to check, no scramble — just the context you need to start your day focused.",
  },
  {
    question: "Can GAIA monitor my sales pipeline without me asking?",
    answer:
      "Yes — GAIA's proactive mode watches your HubSpot or Salesforce pipeline continuously. When a deal shows signs of going cold (no activity, no response), GAIA alerts you and drafts a follow-up email automatically.",
  },
  {
    question: "Is GAIA open source?",
    answer:
      "Yes. GAIA is fully open source, available on GitHub under The Experience Company. You can self-host it or use the cloud version at heygaia.io.",
  },
  {
    question: "How long does it take to set up GAIA for a startup?",
    answer:
      "Most founders are fully set up within 30 minutes. Connect your tools, configure your daily briefing preferences, and GAIA starts working immediately. There's no complex workflow builder required.",
  },
];

export const SOFTWARE_DEV_FAQS = [
  {
    question: "Can GAIA automatically write my daily standup?",
    answer:
      "Yes. GAIA monitors your GitHub commits, Linear tickets, and Slack activity, then generates a concise standup report (what you did, what you're doing, blockers) every morning. You review and post in 30 seconds.",
  },
  {
    question: "How does GAIA help with GitHub PR triage?",
    answer:
      "GAIA monitors your GitHub notifications, prioritizes PRs that need your attention, summarizes review comments, and surfaces critical issues — so you check GitHub once a day with full context instead of constantly.",
  },
  {
    question: "What integrations does GAIA have for software developers?",
    answer:
      "GAIA integrates with GitHub, Linear, Slack, Jira, Notion, Sentry, and Datadog. It creates a unified view of your dev workflow across all these tools and surfaces what actually needs your attention.",
  },
  {
    question: "Does GAIA help reduce context switching for developers?",
    answer:
      "That's its primary purpose for developers. Instead of checking GitHub, Linear, Slack, and email separately throughout the day, GAIA aggregates what matters and surfaces it in one briefing — protecting your deep work time.",
  },
  {
    question: "Can GAIA monitor production incidents and alert me?",
    answer:
      "Yes. GAIA integrates with Sentry and Datadog to monitor error rates and incidents, and sends alerts to Slack or email with context about what changed and potential causes.",
  },
  {
    question: "Is GAIA useful for solo developers or only for teams?",
    answer:
      "Both. Solo developers use GAIA to manage their GitHub and project tools without distraction. Teams use GAIA via the Slack bot — team members can ask @GAIA questions and get instant answers from GitHub, Linear, and docs.",
  },
  {
    question: "How does GAIA's Slack bot work for dev teams?",
    answer:
      "Your team asks @GAIA anything in Slack — PR status, ticket context, deployment details — and GAIA answers from your connected GitHub, Linear, and Notion. No more pulling the senior dev into every thread.",
  },
  {
    question:
      "Can GAIA integrate with Linear for developer workflow automation?",
    answer:
      "Yes. GAIA integrates deeply with Linear to monitor ticket status, track sprint progress, surface blocked issues, and include Linear context in your daily standup and briefings.",
  },
];

export const SALES_FAQS = [
  {
    question: "How does GAIA help sales professionals manage their pipeline?",
    answer:
      "GAIA monitors your HubSpot or Salesforce pipeline continuously, identifies deals at risk of going cold, and drafts follow-up emails before you even remember to check. You stay on top of every deal without living in your CRM.",
  },
  {
    question: "Can GAIA prepare me for sales calls automatically?",
    answer:
      "Yes. Before each call, GAIA pulls deal history, recent activity, company news, and contact context from your CRM and LinkedIn — and delivers a concise brief so you walk in prepared, every time.",
  },
  {
    question: "Does GAIA integrate with HubSpot and Salesforce?",
    answer:
      "Yes. GAIA integrates natively with HubSpot, Salesforce, and other popular CRMs. It monitors deal stages, contact activity, and pipeline health continuously without you needing to check.",
  },
  {
    question: "Can GAIA draft follow-up emails for my deals?",
    answer:
      "Yes. When GAIA detects a deal hasn't had activity in a set period, it drafts a personalized follow-up email based on the deal context and your previous correspondence. You approve and send in one click.",
  },
  {
    question: "How much time can I save using GAIA as a sales rep?",
    answer:
      "Sales reps typically save 5-10 hours per week on CRM updates, follow-up drafting, and meeting prep. GAIA handles the admin so you can spend more time in actual sales conversations.",
  },
  {
    question: "Does GAIA update my CRM automatically?",
    answer:
      "Yes. After calls and email exchanges, GAIA can log activity, update deal stages, and add notes to your CRM — keeping your records current without you touching them manually.",
  },
  {
    question: "Can GAIA help me prioritize which deals to focus on?",
    answer:
      "Yes. GAIA analyzes your pipeline for deal velocity, engagement signals, and inactivity — then surfaces a prioritized list of deals that need your attention each morning.",
  },
];

export const PM_FAQS = [
  {
    question: "How does GAIA help product managers with stakeholder updates?",
    answer:
      "GAIA pulls data from Linear, GitHub, and Notion to generate weekly stakeholder update drafts automatically. You spend 10 minutes reviewing instead of an hour writing.",
  },
  {
    question: "Can GAIA triage feature requests from multiple channels?",
    answer:
      "Yes. GAIA monitors Slack, email, and Intercom for feature requests, categorizes them by theme, and surfaces the most-requested items — giving you a prioritized backlog without manual triage.",
  },
  {
    question: "Does GAIA integrate with Linear for product managers?",
    answer:
      "Yes. GAIA deeply integrates with Linear to track sprint progress, surface blocked tickets, generate sprint reports, and help you run your planning process with less overhead.",
  },
  {
    question: "Can GAIA generate sprint reports automatically?",
    answer:
      "Yes. At the end of each sprint, GAIA pulls completed tickets, velocity data, and blocker history from Linear and GitHub, then generates a comprehensive sprint report ready to share with stakeholders.",
  },
  {
    question: "How does GAIA help PMs prepare for planning meetings?",
    answer:
      "GAIA aggregates open feature requests, in-progress tickets, engineering blockers, and customer feedback threads — and delivers a pre-meeting brief so you walk into planning with full context.",
  },
  {
    question: "Can GAIA help me write product briefs and PRDs?",
    answer:
      "Yes. You can ask GAIA to draft product briefs from context in your Notion, Slack threads, and Linear tickets. It synthesizes scattered context into structured documents.",
  },
  {
    question: "What tools does GAIA connect to for product managers?",
    answer:
      "GAIA integrates with Linear, GitHub, Notion, Slack, Jira, Confluence, Intercom, and Google Workspace — the core stack for most product teams.",
  },
];

export const EM_FAQS = [
  {
    question: "How does GAIA help engineering managers prepare for 1:1s?",
    answer:
      "Before each 1:1, GAIA prepares a brief with the engineer's recent PR activity, Linear ticket progress, blockers, and Slack context — so you walk in with full situational awareness and can focus on the conversation, not status collection.",
  },
  {
    question: "Can GAIA track team velocity and PR cycle times automatically?",
    answer:
      "Yes. GAIA monitors GitHub and Linear continuously, tracking PR cycle times, review turnaround, merge rates, and sprint velocity — and surfaces anomalies before they become problems.",
  },
  {
    question: "Does GAIA help with sprint reporting for engineering managers?",
    answer:
      "Yes. GAIA generates weekly and sprint-level reports from GitHub and Linear data automatically. Share them with stakeholders without spending hours in Confluence.",
  },
  {
    question: "How does GAIA surface blockers for engineering managers?",
    answer:
      "GAIA monitors PRs sitting in review too long, tickets blocked with no activity, and Slack threads that haven't been resolved — and proactively surfaces these in your daily briefing so you can unblock your team.",
  },
  {
    question: "Can GAIA help me stay technical while managing a team?",
    answer:
      "Yes. GAIA keeps you informed on code quality, PR reviews, and architecture decisions across your team's repos — giving you enough technical context to contribute meaningfully without reading every line.",
  },
  {
    question: "What integrations does GAIA have for engineering managers?",
    answer:
      "GAIA integrates with GitHub, Linear, Jira, Slack, Notion, and Confluence — covering the full EM workflow from team communication to sprint tracking to documentation.",
  },
  {
    question: "How does GAIA's Slack bot help engineering teams?",
    answer:
      "Team members ask @GAIA in Slack for PR status, deployment context, or ticket details — and get instant answers from your connected GitHub, Linear, and docs. This reduces interruptions to you and senior engineers.",
  },
];

export const AGENCY_FAQS = [
  {
    question: "How does GAIA help agency owners manage multiple clients?",
    answer:
      "GAIA monitors all your client projects across ClickUp, Asana, or Notion simultaneously, generating weekly status reports, flagging at-risk deliverables, and keeping your pipeline active — automatically.",
  },
  {
    question: "Can GAIA write client status reports automatically?",
    answer:
      "Yes. GAIA pulls project data from your project management tool, structures it into a professional status report, and sends it to clients on schedule. You review and approve, or let it run fully automated.",
  },
  {
    question: "Does GAIA integrate with ClickUp and Asana for agencies?",
    answer:
      "Yes. GAIA integrates with ClickUp, Asana, Monday.com, and Notion — the most popular project management tools used by digital agencies.",
  },
  {
    question: "How does GAIA help with agency business development?",
    answer:
      "GAIA monitors your BD pipeline in your CRM, alerts you to deals going cold, and drafts outreach messages for warm leads — so you never let a potential client slip through the cracks.",
  },
  {
    question:
      "Can GAIA help me scale my agency without hiring more project managers?",
    answer:
      "Yes. The operational work that typically requires a project manager — status reports, client updates, timeline monitoring, risk alerts — GAIA handles automatically. Agencies run 10+ clients with the same overhead as managing 5.",
  },
  {
    question: "How does GAIA monitor client project health?",
    answer:
      "GAIA tracks task completion rates, overdue milestones, budget burn, and team activity across all client projects. It surfaces at-risk projects proactively so you can course-correct before a client notices.",
  },
  {
    question: "Can GAIA handle client communication on my behalf?",
    answer:
      "GAIA can draft client emails, status updates, and meeting summaries based on project data and context. You review and send, keeping communication consistent and timely without writing every message from scratch.",
  },
];
